import React, { useState, useEffect } from "react";
import {
  Badge,
  IconButton,
  Tooltip,
  Box,
  Menu,
  MenuItem,
  Typography,
  Divider,
} from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { fetchFileListing } from "../services/apiService";

const fetchedNotificationSessions = new Set();

const NotificationBell = ({ isAuthenticated = false }) => {
  const [hasNotifications, setHasNotifications] = useState(false);
  const [previousFiles, setPreviousFiles] = useState([]);
  const [notificationCount, setNotificationCount] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [notifiedFiles, setNotifiedFiles] = useState(new Set()); // Track already notified files
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
    // Clear notification indicator when menu is opened
    setHasNotifications(false);
    setNotificationCount(0);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  // Function to normalize file arrays for comparison
  const normalizeFiles = (files) => {
    return files.map((file) =>
      typeof file === "string" ? file : file.file_name || file
    );
  };

  // Helper function to extract completed files from API response
  const extractCompletedFiles = (response) => {
    if (response.data && Array.isArray(response.data)) {
      return response.data
        .filter((item) => item.status === "completed")
        .map((item) => item.filename);
    }
    return response.filenames || [];
  };

  // Helper function to update files state
  const updateFilesState = (currentFiles) => {
    setPreviousFiles(currentFiles);
    localStorage.setItem("previousFiles", JSON.stringify(currentFiles));
  };

  const checkForFileChanges = async () => {
    if (!localStorage.getItem('accessToken')) return;
    try {
      const response = await fetchFileListing();

      if (response.error) {
        console.error("Error fetching files:", response.message);
        return;
      }

      const currentFiles = extractCompletedFiles(response);
      const savedPrevFiles = JSON.parse(localStorage.getItem("previousFiles") || "[]");
      const filesToCompare = previousFiles.length > 0 ? previousFiles : savedPrevFiles;

      if (filesToCompare.length > 0) {
        const oldNormalized = new Set(normalizeFiles(filesToCompare));
        const currentNormalized = normalizeFiles(currentFiles);
        const newFiles = currentFiles.filter((file, index) => !oldNormalized.has(currentNormalized[index]));
        const newNotNotifiedFiles = newFiles.filter((file) => {
          const fileName = typeof file === "string" ? file : file.file_name || file;
          return !notifiedFiles.has(fileName);
        });

        if (newNotNotifiedFiles.length > 0) {
          setHasNotifications(true);
          setNotificationCount((prev) => prev + newNotNotifiedFiles.length);

          const newNotifications = newNotNotifiedFiles.map((file) => {
            const fileName = typeof file === "string" ? file : file.file_name || file;
            setNotifiedFiles((prev) => new Set(prev).add(fileName));
            return {
              id: Date.now() + Math.random(),
              message: `New file available: ${fileName}`,
              timestamp: new Date().toLocaleDateString() + " " + new Date().toLocaleTimeString(),
              fileName,
            };
          });

          setNotifications((prev) => [...newNotifications, ...prev].slice(0, 10));
        }
      }

      updateFilesState(currentFiles);
    } catch (error) {
      console.error("Error checking for file changes:", error);
    }
  };

  // Helper function to load saved files from localStorage
  const loadSavedFiles = () => {
    return JSON.parse(localStorage.getItem("previousFiles") || "[]");
  };

  // Helper function to initialize notified files from saved notifications
  const initializeNotifiedFiles = () => {
    try {
      const savedNotifications = JSON.parse(
        localStorage.getItem("notifications") || "[]"
      );
      const initialNotifiedFiles = new Set(
        savedNotifications.map((n) => n.fileName).filter(Boolean)
      );
      setNotifiedFiles(initialNotifiedFiles);
      setNotifications(savedNotifications);
    } catch (e) {
      console.error("Error loading saved notifications:", e);
    }
  };

  useEffect(() => {
    const savedFiles = loadSavedFiles();
    initializeNotifiedFiles();

    if (savedFiles.length > 0) {
      setPreviousFiles(savedFiles);
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    const accessToken = localStorage.getItem("accessToken");
    if (!accessToken || fetchedNotificationSessions.has(accessToken)) {
      return;
    }

    fetchedNotificationSessions.add(accessToken);
    checkForFileChanges();
  }, [isAuthenticated]);

  // Save notifications to localStorage when they change
  useEffect(() => {
    localStorage.setItem("notifications", JSON.stringify(notifications));
  }, [notifications]);

  // Clear all notifications handler
  const handleClearNotifications = () => {
    setNotifications([]);
    // Don't reset notifiedFiles to prevent re-notifying for the same files
  };

  // Update formatTimestamp to also include elapsed time information and seconds
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "Unknown";

    try {
      const date = new Date(timestamp);
      
      // Format the date in IST time zone (UTC+5:30)
      const formattedDate = new Intl.DateTimeFormat("en-IN", {
        year: "numeric",
        month: "short",
        day: "2-digit",
        timeZone: "Asia/Kolkata",
      }).format(date);

      return formattedDate;
    } catch (e) {
      return timestamp; // Return the original timestamp if parsing fails
    }
  };

  // Helper component for notification header
  const renderNotificationHeader = () => (
    <Box
      sx={{
        p: 2,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <Typography variant="subtitle1" fontWeight="bold">
        Notifications
      </Typography>
      {notifications.length > 0 && (
        <Typography
          variant="caption"
          color="primary"
          sx={{ cursor: "pointer" }}
          onClick={handleClearNotifications}
        >
          Clear all
        </Typography>
      )}
    </Box>
  );

  // Helper component for empty notifications state
  const renderEmptyNotifications = () => (
    <MenuItem disabled>
      <Typography variant="body2" color="text.secondary">
        No new notifications
      </Typography>
    </MenuItem>
  );

  // Helper component for individual notification item
  const renderNotificationItem = (notification) => (
    <MenuItem key={notification.id} onClick={handleClose}>
      <Box sx={{ width: "100%" }}>
        <Typography variant="body2">
          <Typography>
            <CheckCircleIcon
              sx={{ color: "green", fontSize: "0.9rem" }}
            />{" "}
          </Typography>
          <Typography
            sx={{
              fontSize: "0.9rem",
              position: "absolute",
              top: "8px",
              left: "35px",
              maxWidth: "250px",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
            title={notification.fileName}
          >
            {notification.fileName}
          </Typography>
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {formatTimestamp(notification.timestamp)}
        </Typography>
      </Box>
    </MenuItem>
  );

  // Helper function to render notification list
  const renderNotificationList = () => {
    if (notifications.length === 0) {
      return renderEmptyNotifications();
    }
    return notifications.map(renderNotificationItem);
  };

  return (
    <Box>
      <Tooltip title="Notifications">
        <IconButton
          onClick={handleClick}
          size="large"
          aria-controls={open ? "notifications-menu" : undefined}
          aria-haspopup="true"
          aria-expanded={open ? "true" : undefined}
          sx={{
            color: hasNotifications ? "warning.main" : "action.active",
            animation: hasNotifications ? "pulse 1.5s infinite" : "none",
            "@keyframes pulse": {
              "0%": { transform: "scale(1)" },
              "50%": { transform: "scale(1.2)" },
              "100%": { transform: "scale(1)" },
            },
          }}
        >
          <Badge badgeContent={notificationCount} color="error">
            <NotificationsIcon />
          </Badge>
        </IconButton>
      </Tooltip>

      <Menu
        id="notifications-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": "notification-button",
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        PaperProps={{
          elevation: 3,
          sx: {
            minWidth: 300,
            maxWidth: 400,
            maxHeight: 400,
            overflow: "auto",
            mt: 1.5,
            right: "auto",
            left: "-250px",
            "& .MuiMenuItem-root": {
              px: 2,
              py: 1,
              borderBottom: "1px solid rgba(0,0,0,0.05)",
            },
          },
        }}
      >
        {renderNotificationHeader()}
        <Divider />
        {renderNotificationList()}
      </Menu>
    </Box>
  );
};

export default NotificationBell;
